import {
  createOpenHyperlinkPopoverAction,
  createOpenInformationPopoverAction,
  createOpenLabelPopoverAction,
  createOpenNotePopoverAction,
} from 'app/services/action/ui-actions/frame-actions'
import { createOpenHrefAction } from './embed-open-href-action'
import { createAddLocalImageAction } from './embed-add-local-image-action'
import { createAddLocalAttachmentAction } from './embed-add-local-attachment-action'
import { createOpenTopicLinkDialogAction } from './embed-open-topic-link-dialog-action'
import { createAddEquationAction } from './embed-add-equation-action'
import { openImportImageDialog } from 'app/services/action/dialog/import-image-dialog'

type ActionGroup = 'common' | 'ui' | 'dialog'
export class Action {
  appendBeforeHandler: (commandId: string, assistantHandler: Function) => void
  appendAfterHandler: (commandId: string, assistantHandler: Function) => void
  appendAfterAllHandler: (afterAllHandler: Function) => void

  constructor() {
    const beforeHandlerFn = createAssistantHandlerFn()
    const beforeHandleAction = beforeHandlerFn.assistantHandleCommand
    this.appendBeforeHandler = beforeHandlerFn.appendAssistantHandler

    const afterHandlerFn = createAssistantHandlerFn()
    const afterHandleAction = afterHandlerFn.assistantHandleCommand
    this.appendAfterHandler = afterHandlerFn.appendAssistantHandler

    const afterAllHandlerFn = createAfterAllHandlerFn()
    const afterAllHandleAction = afterAllHandlerFn.afterAllHandleCommand
    this.appendAfterAllHandler = afterAllHandlerFn.appendAfterAllHandler

    const allActions: Record<string, any> = {
      ui: this.ui,
      common: this.common,
      dialog: this.dialog,
    }

    for (const actionGroup of Object.keys(allActions)) {
      for (const actionName of Object.keys(allActions[actionGroup])) {
        const action = allActions[actionGroup][actionName]

        ;(this[actionGroup as ActionGroup] as Record<string, any>)[actionName] = new Proxy(action, {
          get(target, key, receiver) {
            if (key === 'execute') {
              const executeHandler = Reflect.get(target, key, receiver)
              return async (...args: any[]) => {
                let actionRes: any = null
                let res = await beforeHandleAction(actionName, ...args)
                if (res !== 'interruption') {
                  actionRes = res = await executeHandler(...args)
                }
                if (res !== 'interruption') {
                  res = await afterHandleAction(actionName, ...args)
                }
                if (res !== 'interruption') {
                  await afterAllHandleAction(actionName, ...args)
                }
                return actionRes
              }
            }
            return Reflect.get(target, key, receiver)
          },
        })
      }
    }
  }

  public common = {
    openHref: createOpenHrefAction(null),
    addLocalImage: createAddLocalImageAction(),
    addLocalAttachment: createAddLocalAttachmentAction(),
    openTopicLinkDialog: createOpenTopicLinkDialogAction(),
    addEquation: createAddEquationAction(),
  }

  // Action using to control UI
  public ui = {
    openHyperlinkPopover: createOpenHyperlinkPopoverAction(null),
    openLabelPopover: createOpenLabelPopoverAction(null),
    openNotePopover: createOpenNotePopoverAction(null),
    openInformationPopover: createOpenInformationPopoverAction(null),
  }

  public dialog = {
    openImportImageDialog,
  }
}

function createAfterAllHandlerFn() {
  const afterAllHandlers: { forAllCommands: Function | null } = {
    forAllCommands: null,
  }
  const appendAfterAllHandler = (afterAllHandler: Function) => {
    const existedAfterAllHandler = afterAllHandlers['forAllCommands']
    afterAllHandlers['forAllCommands'] = async (...args: any[]) => {
      const interruption = existedAfterAllHandler && (await existedAfterAllHandler(...args))
      if (!interruption) {
        return await afterAllHandler(...args)
      } else {
        return interruption
      }
    }
  }
  const afterAllHandleCommand = (commandId: string, ...handlerArgs: any[]) => {
    const handler = afterAllHandlers['forAllCommands']
    return handler ? handler(commandId, handlerArgs) : Promise.resolve()
  }
  return {
    appendAfterAllHandler,
    afterAllHandleCommand,
  }
}

function createAssistantHandlerFn() {
  const assistantHandlers: Record<string, Function> = {}

  const appendAssistantHandler = (commandId: string, assistantHandler: Function) => {
    const existedAssistantHandler = assistantHandlers[commandId]
    assistantHandlers[commandId] = async (...args: any[]) => {
      const interruption = existedAssistantHandler && (await existedAssistantHandler(...args))
      if (!interruption) {
        return await assistantHandler(...args)
      } else {
        return interruption
      }
    }
  }
  const assistantHandlesProxy = new Proxy(assistantHandlers, {
    get(target, property) {
      const handlerFn = Reflect.get(target, property)
      return typeof handlerFn === 'function' ? handlerFn : () => Promise.resolve()
    },
  })

  const assistantHandleCommand = (commandId: string, ...handlerArgs: any[]) => {
    const assistantHandler = assistantHandlesProxy[commandId]
    return assistantHandler(commandId, handlerArgs)
  }

  return {
    appendAssistantHandler,
    assistantHandleCommand,
  }
}
